/* SHARED CFS COMPUTE ENGINE */
// ★ A1 — the runtime CFS rows (fundraising rounds, implementation-deferred revenue) are
// spliced by THE ONE injector, which CashFlowPage calls too. Each injected row mirrors an
// injected BS row via `bsKey`, which is why both injectors live in one file (Mantra #7).
import { injectDynamicCFSLines } from './dynamicLines';

const r2 = n => Math.round(n * 100) / 100;
// ★ SWEEP B2-ii: the hardcoded 2024-2026 ALL_M window is DELETED. It was Semitek's
// model window used as a back-compat default; every caller passes allMonths
// (runFullModel always does) and no page imports the export — verified. An
// absent window now yields an empty one (honest-empty), not a foreign calendar.
const prevMonth = (months, m) => { const i = months.indexOf(m); return i > 0 ? months[i-1] : null; };


// ★ SWEEP B4b (A8): the hardcoded SEMITEK cash-flow structure that used to live here
// is DELETED — its only consumers were the two `|| CFS_LINES` fallbacks (this engine
// and CashFlowPage), both now honest-null.


// Sweep-schedule display metadata for the CFS page's Sweep tab.
//
// LABEL/RENDER-HINT ONLY — computeCFS builds its SW result off a hardcoded key
// list (see the SW init below), never off this array, so these labels have zero
// numeric effect. Two of the fifteen labels NAME the tenant's SWEEP-TARGET
// ACCOUNT and take ${sweepLabel} (mm_h, mm_d — previously hardcoded to Semitek's
// "Money Market"); sweepLabel comes from bs_actuals_meta[role='sweep_target'].label,
// passed in by the caller. The rest are generic-financial and tenant-safe as-is.
//
// sw_cb deliberately keeps the SHORT generic form "Cash Before MM Repayment /
// (Borrowing)": templating the full account name reads awkwardly ("Cash Before
// Chase Money Market Repayment / (Borrowing)") and the row's meaning is
// unambiguous on its own, sitting directly above the named account's schedule.
//
// Returns a FRESH array each call, so no tenant's label can leak into the next.
export function buildSWLines(sweepLabel) {
  const sw = sweepLabel || "Money Market";
  return [
    {id:"sw_h",label:"Sweep Schedule",level:0,type:"header"},
    {id:"sw_ca",label:"Cash Available/(Required) Before Sweep",level:1},
    {id:"sw_bc",label:"Plus: Beginning Cash on Balance Sheet",level:1},
    {id:"sw_mc",label:"Less: Minimum Cash Balance",level:1},
    {id:"sw_cb",label:"Cash Before MM Repayment / (Borrowing)",level:1,type:"total"},
    {id:"sw_s1",label:"",level:0,type:"spacer"},
    {id:"mm_h",label:sw,level:0,type:"header"},
    {id:"mm_b",label:"Beginning Balance",level:1},
    {id:"mm_d",label:`${sw} Replenishment / (Drawdown)`,level:1},
    {id:"mm_e",label:"Ending Balance",level:1,type:"total",bold:true},
    {id:"sw_s2",label:"",level:0,type:"spacer"},
    {id:"int_h",label:"Interest Schedule",level:0,type:"header"},
    {id:"int_r",label:"Interest Rate",level:1,fmtPct:true},
    {id:"sw_s3",label:"",level:0,type:"spacer"},
    {id:"int_i",label:"Interest Income",level:1},
  ];
}

const FONT="'DM Sans',-apple-system,sans-serif";const BLUE="#2A6DB0";const BLACK="#1A1C2E";
const SL={position:"sticky",left:0,zIndex:5,background:"inherit"};
const MR_OPTS=[{v:"none",l:"None (exact)"},{v:"1000",l:"$1,000"},{v:"10000",l:"$10,000"}];
const mrA=(v,i)=>{if(i<=0)return v;return v>=0?Math.ceil(v/i)*i:Math.floor(v/i)*i;};


export function computeCFS(cutoff, ss, dec23, openingMMBalance, bsData, isRef, sweepAct, oneoffs, finZero, frLines, implDeferredLines, allMonths, openingMonth, cfsLines){
  const MONTHS = allMonths || [];
  // openingMonth is always supplied by the orchestrator (runFullModel derives it
  // via subtractOneMonth(allMonths[0]); ModelProvider's config-re-run path passes
  // rawRef.openingMonth). The former '2023-12' fallback was a dead Semitek literal.
  const OPEN = openingMonth;
  const{minCash,mround,rate}=ss;
  const R={},SW={};
  const bv=(k,m)=>m===OPEN?r2(dec23[k]||0):r2(bsData[k]?.[m]||0);

  // Phase 3b: accept dynamic cfsLines from buildCFSLinesForCompany. Legacy
  // ★★ SWEEP B4b (A8) — the CFS twin of computeBS's guard. Was `cfsLines || CFS_LINES`,
  // i.e. a meta-less tenant's cash flow computed on Semitek's statement structure.
  // Honest-empty instead; the orchestrator records missingChartOfAccounts.
  if (!cfsLines) return { cfs: {}, sw: {} };
  // ★ A1 — ONE injector, shared with CashFlowPage. Returns `cfsLines` BY REFERENCE when there
  // is nothing to inject, preserving the old no-copy fast path exactly. The mm_sweep-LAST
  // rebuild of total_fin.sumOf now lives there, named, instead of inline here.
  const lines = injectDynamicCFSLines(cfsLines, frLines, implDeferredLines);

  lines.forEach(l=>{
    if(l.type==="header"||l.type==="spacer")return;
    R[l.id]={};
    MONTHS.forEach(m=>{
      const pm=prevMonth(MONTHS, m)||OPEN;
      if(l.source==="is")R[l.id][m]=r2(isRef[l.isKey]?.[m]||0);
      else if(l.source==="bs"){const c=bv(l.bsKey,m),p=bv(l.bsKey,pm),d=r2(c-p);R[l.id][m]=finZero[l.id]?.[m]?0:(l.sign==="a"?r2(-d):d);}
      else if(l.source==="oneoff")R[l.id][m]=r2(oneoffs[l.id]?.[m]||0);
      else if(l.source==="fa_writeoff"){
        // Non-cash FA write-off. DERIVE: −Σ over gross fixed-asset BS lines of max(0, prev−curr)
        // — a gross DROP is a disposal (gross only rises with purchases; depreciation hits the
        // separate accumdep contra). AUTO-APPLIES; offsets the phantom investing inflow the gross
        // drop creates so the swept MM/forecast plug zero out. OVERRIDE: cfs_oneoffs.fa_writeoff[m]
        // — if present (incl. 0) it REPLACES the derived value (escape hatch for sales w/ cash
        // proceeds and partial-NBV disposals the gross-drop can't infer; the future close module's
        // entry slot). Tenants with no FA drops derive 0 → byte-identical.
        const ov=oneoffs[l.id]?.[m];
        if(ov!==undefined)R[l.id][m]=r2(ov);
        else{let drop=0;(l.gross_keys||[]).forEach(k=>{const dd=r2(bv(k,pm)-bv(k,m));if(dd>0)drop=r2(drop+dd);});R[l.id][m]=r2(-drop);}
      }
      else if(l.sumOf)R[l.id][m]=r2(l.sumOf.reduce((s,c)=>s+(R[c]?.[m]||0),0));
    });
  });

  R.cash_avail={};
  const fIds=lines.filter(l=>l.source==="bs"&&(l.id.startsWith("fin_")||l.id.startsWith("fin_fr_"))).map(l=>l.id);
  MONTHS.forEach(m=>{
    const fin=r2(fIds.reduce((s,id)=>s+(R[id]?.[m]||0),0));
    R.cash_avail[m]=r2((R.total_oper?.[m]||0)+(R.total_invest?.[m]||0)+fin);
  });

  ["sw_ca","sw_bc","sw_mc","sw_cb","mm_b","mm_d","mm_e","int_r","int_i"].forEach(k=>{SW[k]={};});
  R.mm_sweep={};

  const isActual = m => m <= cutoff;
  const fF = MONTHS[MONTHS.indexOf(cutoff) + 1]; // first forecast month

  MONTHS.forEach(m=>{
    const pm = prevMonth(MONTHS, m) || OPEN;

    // 1. Cash available before sweep
    SW.sw_ca[m] = r2(R.cash_avail[m]);

    // 2. Beginning cash on BS
    if (m === MONTHS[0]) SW.sw_bc[m] = r2(dec23.total_clearing || 0);
    else if (isActual(m)) SW.sw_bc[m] = bv("total_clearing", pm);
    else if (m === fF) SW.sw_bc[m] = bv("total_clearing", cutoff);
    else SW.sw_bc[m] = r2(minCash);

    // 3. Less: minimum cash
    SW.sw_mc[m] = isActual(m) ? r2(-bv("total_clearing", m)) : r2(-minCash);

    // 4. Cash before MM
    SW.sw_cb[m] = r2(SW.sw_ca[m] + SW.sw_bc[m] + SW.sw_mc[m]);

    // 5. MM drawdown (actuals = exact, forecast = apply mround)
    if (isActual(m)) {
      SW.mm_d[m] = r2(SW.sw_cb[m]);
    } else {
      const inc = mround === "none" ? 0 : parseInt(mround);
      SW.mm_d[m] = r2(inc > 0 ? mrA(SW.sw_cb[m], inc) : SW.sw_cb[m]);
    }

    // 6. MM beginning balance
    SW.mm_b[m] = r2(m === MONTHS[0] ? (openingMMBalance || 0) : (SW.mm_e[pm] || 0));

    // 7. MM ending balance
    SW.mm_e[m] = r2(SW.mm_b[m] + SW.mm_d[m]);

    // 8-9. Interest (actuals = 0, forecast = computed)
    SW.int_r[m] = isActual(m) ? 0 : rate;
    SW.int_i[m] = isActual(m) ? 0 : r2(SW.mm_b[m] * rate / 12);

    // 10. CFS line
    R.mm_sweep[m] = r2(-SW.mm_d[m]);
  });

  R.total_fin={};
  const tfLine = lines.find(l=>l.id==="total_fin");
  // A1: `baseLines` was an alias for `cfsLines`; the alias went with the inlined injection.
  // Referencing cfsLines directly keeps this fallback byte-identical to its prior behaviour
  // (it is unreachable either way — total_fin is an always-present synthetic total).
  const tfIds = tfLine ? tfLine.sumOf : cfsLines.find(l=>l.id==="total_fin").sumOf;
  MONTHS.forEach(m=>{R.total_fin[m]=r2(tfIds.reduce((s,c)=>s+(R[c]?.[m]||0),0));});

  R.beg_cash={};R.chg_cash={};R.end_cash={};
  MONTHS.forEach(m=>{
    const pm=prevMonth(MONTHS, m)||OPEN;
    R.beg_cash[m]=m===MONTHS[0]?r2(dec23.total_clearing):r2(R.end_cash[pm]||0);
    R.chg_cash[m]=r2((R.total_oper?.[m]||0)+(R.total_invest?.[m]||0)+(R.total_fin?.[m]||0));
    R.end_cash[m]=r2(R.beg_cash[m]+R.chg_cash[m]);
  });

  return {cfs:R,sw:SW};
}

export { prevMonth, r2 };
